import {useIsFeatureActive} from '@aboutyou/feature-flags';
import {PriceAndBrandSection} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {TrackingPromotionCreative} from '@aboutyou/legacy-tracking-sdk';
import {useHistory} from '@aboutyou/router';
import {TrackPromotions, useTrackOriginClick} from '@aboutyou/tracking';
import {
  BrandLogo,
  ContentPopperProps,
  ErrorBoundary,
  PriceLegalInfoPopup,
  SkeletonBlock,
} from '@aboutyou/ui-components';
import {styled} from '@linaria/react';
import React, {useEffect, useMemo} from 'react';
import {SelectedSize} from '../../../features/SizeSelection/hooks/useSelectedSizeState';
import {useBrandLocationWithState} from '../utils';
import {LogPriceDifference} from '../utils/LogPriceDifference';
import {PriceBox} from './PriceBox';
import {SoldOutState} from './SoldOutState';

export interface PriceAndBrandHorizontalViewProps {
  selectedSize: SelectedSize;
  className?: string;
  style?: React.CSSProperties;
  setProductInfo?: (params: {
    productName: string;
    productBrandName: string;
  }) => void;
  PriceLegalInfoLayoutComponent: ContentPopperProps['LayoutComponent'];
  hideBrandLogo?: boolean;
  hideBadges?: boolean;
}

export const PriceAndBrandHorizontalView: React.FC<
  PriceAndBrandHorizontalViewProps & PriceAndBrandSection
> = ({
  brandLogo,
  selectedSize,
  className,
  price,
  priceV2,
  product,
  style,
  sizePricesV2,
  setProductInfo,
  PriceLegalInfoLayoutComponent,
  hideBrandLogo,
  hideBadges,
}) => {
  const trackOriginClick = useTrackOriginClick();
  const history = useHistory();
  const {isActive: isPriceDisplayOverhaulActive} = useIsFeatureActive(
    'ab_price_display_overhaul_v2',
  );
  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setProductInfo?.({
      productBrandName: brandLogo.brand.name,
      productName: product.name,
    });
  }, []);

  const brandPathWithState = useBrandLocationWithState({
    path: brandLogo.brand.path,
    brandPreview: brandLogo.brandPreview,
  });

  const onBrandLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackOriginClick('brands_logo')();

    history.push(brandLogo.path, brandPathWithState.state);
  };

  const usedPrice = useMemo(() => {
    if (sizePricesV2 && selectedSize && isPriceDisplayOverhaulActive) {
      return sizePricesV2[selectedSize.sizeId];
    }

    return isPriceDisplayOverhaulActive ? priceV2 : price;
  }, [
    sizePricesV2,
    selectedSize,
    isPriceDisplayOverhaulActive,
    priceV2,
    price,
  ]);

  if (!usedPrice) {
    return (
      <>
        <LogPriceDifference
          product={product}
          price={usedPrice}
          selectedSize={selectedSize}
          isInMiniADPDialog={false}
        />
        <SoldOutState
          productImage={product.primaryBustImage}
          productName={product.name}
          brandName={brandLogo.brand.name}
          shareUrl={product.shareUrl}
        />
      </>
    );
  }

  return (
    <ErrorBoundary
      ErrorComponent={null}
      renderContext="product_price_and_brand"
    >
      <LogPriceDifference
        product={product}
        price={usedPrice}
        selectedSize={selectedSize}
        isInMiniADPDialog={false}
      />
      <PriceAndBrandWrapper className={className} style={style}>
        {usedPrice ? (
          <StyledPriceBox
            selectedSize={selectedSize}
            productPrice={usedPrice}
            key={product.id}
            isInMiniADPDialog={false}
            hideBadges={hideBadges}
          />
        ) : (
          <SkeletonBlock $width="100%" />
        )}
        {!hideBrandLogo && (
          <TrackPromotions
            promotion={{
              source: TrackingPromotionCreative.BrandReco,
              brand: {
                id: brandLogo.brand.id,
                name: brandLogo.brand.name,
                path: brandLogo.brand.path,
              },
            }}
            streamPosition={NaN}
          >
            <BrandLogo
              image={brandLogo.brand.blackLogoImage}
              name={brandLogo.brand.name}
              path={brandPathWithState}
              onClick={onBrandLogoClick}
              maxHeight="42px"
              maxWidth="200px"
              alignment="right"
            />
          </TrackPromotions>
        )}
      </PriceAndBrandWrapper>
      {PriceLegalInfoLayoutComponent && (
        <PriceLegalInfoPopup
          PriceLegalInfoLayoutComponent={PriceLegalInfoLayoutComponent}
        />
      )}
    </ErrorBoundary>
  );
};

const PriceAndBrandWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledPriceBox = styled(PriceBox)`
  width: 100%;
`;
